import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '@/hooks/reduxHooks';
import { lendersApi } from '@/store/apis/lendersApi';
import {
  chatReducer,
  initialChatState,
  isBusy,
  type CardPart,
  type Turn,
} from './chatReducer';
import { shouldStartNewChat } from './chatSession';
import { newId } from './ids';
import { useAgentClient } from './useAgentClient';
import { isLenderRecord, resolveRecordRef } from './recordRef';
import { shouldStartCrew } from './crewStart';
import type { CardAction, Rating } from './types';

/** The crew turn's user bubble: the user pressed Start. */
export const CREW_TURN_TEXT = 'Start the crew';
/** The import turn's user bubble. */
export const importTurnText = (fileName: string) => `Import rates: ${fileName}`;

/**
 * Wires the panel to the agent service. From the portal it takes exactly two
 * things: the signed-in user's JWT and the URL of the page on screen.
 */
export function useAgentChat() {
  const storeDispatch = useAppDispatch();
  const { pathname } = useLocation();
  const recordRef = useMemo(() => resolveRecordRef(pathname), [pathname]);

  const { client, mode } = useAgentClient();

  const [state, dispatch] = useReducer(
    chatReducer,
    undefined,
    initialChatState,
  );
  // Read at send time, so `send` need not change on every streamed token.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const controllers = useRef(new Set<AbortController>());
  useEffect(() => {
    const live = controllers.current;
    return () => live.forEach((controller) => controller.abort());
  }, []);

  const newChat = useCallback(() => {
    controllers.current.forEach((controller) => controller.abort());
    controllers.current.clear();
    dispatch({ type: 'reset' });
  }, []);

  // One server session must never span two clients' records: when the record
  // on screen changes, drop the session id and the turns.
  useEffect(() => {
    if (shouldStartNewChat(stateRef.current.turns, recordRef)) newChat();
  }, [recordRef, newChat]);

  /**
   * Streams a run's events into a turn until done. A failure ends the turn with
   * a safe message; nothing is reported after an abort.
   */
  const streamRun = useCallback(
    async (turnId: string, runId: string, controller: AbortController) => {
      if (!client) return;
      try {
        await client.streamEvents(
          runId,
          {
            onEvent: (event) => dispatch({ type: 'event', turnId, event }),
            onReconnect: () => dispatch({ type: 'reconnecting', turnId }),
          },
          controller.signal,
        );
        if (!controller.signal.aborted) dispatch({ type: 'finished', turnId });
      } catch (error) {
        if (!controller.signal.aborted) {
          dispatch({ type: 'failed', turnId, error });
        }
      }
    },
    [client],
  );

  const send = useCallback(
    async (text: string) => {
      const message = text.trim();
      if (!message || !client) return;
      // The effect above normally got there first; this covers a send in the
      // same tick as the navigation.
      const fresh = shouldStartNewChat(stateRef.current.turns, recordRef);
      if (fresh) newChat();
      else if (controllers.current.size > 0) return; // the last answer is still running
      const sessionId = fresh ? null : stateRef.current.sessionId;

      const turnId = newId();
      dispatch({ type: 'send', turnId, text: message, recordRef });
      const controller = new AbortController();
      controllers.current.add(controller);
      try {
        // sessionId is null on the first turn; the server issues one and we keep it.
        const accepted = await client.startChat(
          { message, recordRef, sessionId },
          controller.signal,
        );
        dispatch({
          type: 'started',
          turnId,
          runId: accepted.runId,
          sessionId: accepted.sessionId,
        });
        await streamRun(turnId, accepted.runId, controller);
      } catch (error) {
        // Aborted by New chat, a record change or unmount: nobody is waiting.
        // Otherwise hand the raw error to the reducer, which keeps only its
        // safe message (never error.message, never anything with the token).
        if (!controller.signal.aborted) {
          dispatch({ type: 'failed', turnId, error });
        }
      } finally {
        controllers.current.delete(controller);
      }
    },
    [client, recordRef, newChat, streamRun],
  );

  /** Drops a lender's rate sheet: an import turn, streamed like `send`. */
  const importSheet = useCallback(
    async (file: File) => {
      if (!client || !recordRef || !isLenderRecord(recordRef)) return;
      const fresh = shouldStartNewChat(stateRef.current.turns, recordRef);
      if (fresh) newChat();
      else if (controllers.current.size > 0) return; // the last answer is still running
      const sessionId = fresh ? null : stateRef.current.sessionId;

      const turnId = newId();
      dispatch({
        type: 'send',
        turnId,
        text: importTurnText(file.name),
        recordRef,
      });
      const controller = new AbortController();
      controllers.current.add(controller);
      try {
        const accepted = await client.uploadPricingSheet(
          { file, lenderId: recordRef.id, sessionId },
          controller.signal,
        );
        dispatch({
          type: 'started',
          turnId,
          runId: accepted.runId,
          sessionId: accepted.sessionId,
        });
        await streamRun(turnId, accepted.runId, controller);
      } catch (error) {
        if (!controller.signal.aborted) {
          dispatch({ type: 'failed', turnId, error });
        }
      } finally {
        controllers.current.delete(controller);
      }
    },
    [client, recordRef, newChat, streamRun],
  );

  /** Start/Cancel on the offer card, Approve/Reject on the pack (Plan 4a section 2). */
  const act = useCallback(
    async (
      turn: Turn,
      card: CardPart,
      action: CardAction,
      reason?: string,
      selected?: string[],
    ) => {
      if (!client || !turn.runId) return;
      // Re-entry guard: a card whose POST is in flight ignores a second click.
      const current = stateRef.current.turns
        .find((t) => t.id === turn.id)
        ?.parts.find(
          (part) =>
            part.kind === 'approval_card' && part.cardId === card.cardId,
        );
      if (current?.kind === 'approval_card' && current.cardState === 'sending')
        return;
      // The POST shares the turn's abortable controller set: New chat, a record
      // change or unmount cancels it, and then nothing follows.
      const controller = new AbortController();
      controllers.current.add(controller);
      try {
        dispatch({
          type: 'card_sending',
          turnId: turn.id,
          cardId: card.cardId,
        });
        let crewRunId: string | null = null;
        try {
          const result = await client.cardAction(
            turn.runId,
            card.cardId,
            action,
            reason,
            controller.signal,
            selected,
          );
          if (controller.signal.aborted) return;
          dispatch({
            type: 'card_done',
            turnId: turn.id,
            cardId: card.cardId,
            action,
            approval: result.approvalRecord,
            pricing: result.pricing,
          });
          // An open pricing tab on this lender shows the new rows.
          if (result.pricing?.status === 'applied') {
            storeDispatch(
              lendersApi.util.invalidateTags(
                result.pricing.productIds.map((id) => ({
                  type: 'LenderPricing' as const,
                  id,
                })),
              ),
            );
          }
          crewRunId = shouldStartCrew(controller.signal, result);
        } catch (error) {
          // Aborted: nobody is waiting. Otherwise the reducer keeps only a safe
          // message, and card_closed keeps the card closed.
          if (!controller.signal.aborted) {
            dispatch({
              type: 'card_failed',
              turnId: turn.id,
              cardId: card.cardId,
              error,
            });
          }
          return;
        }
        if (!crewRunId) return;
        // The crew streams into its own turn, on the chat's session.
        const turnId = newId();
        dispatch({
          type: 'send',
          turnId,
          text: CREW_TURN_TEXT,
          recordRef: turn.recordRef,
        });
        dispatch({ type: 'started', turnId, runId: crewRunId });
        await streamRun(turnId, crewRunId, controller);
      } finally {
        controllers.current.delete(controller);
      }
    },
    [client, streamRun, storeDispatch],
  );

  const rate = useCallback(
    (turn: Turn, rating: Rating) => {
      if (!turn.runId || !client) return;
      dispatch({ type: 'rate', turnId: turn.id, rating });
      void client.sendFeedback(turn.runId, rating);
    },
    [client],
  );

  return {
    state,
    busy: isBusy(state),
    send,
    importSheet,
    act,
    rate,
    newChat,
    recordRef,
    mode,
    client,
  };
}
